import {
    createEventHook, type EventHookOn, type MaybeRefOrGetter, toValue, tryOnMounted,
    tryOnUnmounted,
} from '@vueuse/core';
import {
    computed,
    type ComputedRef,
    type DebuggerOptions,
    nextTick,
    ref,
    unref,
    watch,
} from 'vue';

export type ComponentState = {
    frozen: ComputedRef<boolean>;
    freeze: (frozen?: boolean) => void;
    update: () => Promise<unknown>;
    onUpdate: EventHookOn<unknown>;
};

export type ComponentStateOptions = {
    id?: string;
    initialUpdate?: boolean;
};

type OffFunction = () => void;
const DEBUG = false;

export function useComponentState(
    { id: debugId, initialUpdate }: ComponentStateOptions = {},
    _parent?: MaybeRefOrGetter<ComponentState | undefined>,
    _frozen?: MaybeRefOrGetter<boolean | undefined>,
) {
    const thisFrozen = ref(false);
    const updateHook = createEventHook();
    let updatePending = false;
    let updateProcessing: Promise<unknown> | undefined;

    const localFrozen = computed(() => toValue(_frozen) || unref(thisFrozen));
    const globalFrozen = computed(() => unref(localFrozen) || unref(toValue(_parent)?.frozen) || false);

    function freeze(frozen?: boolean) {
        thisFrozen.value = frozen ?? true;
    }

    const debugOptions: DebuggerOptions | undefined = DEBUG ? {
        onTrigger: (event) => {
            const key = event.key || '';
            console.log(`useComponentState value changed ${debugId}@${key}: ${event.oldValue} => ${event.newValue}`);
        },
    } : undefined;

    function triggerUpdate() {
        if(updateProcessing) {
            return updateProcessing;
        }
        updateProcessing = new Promise((resolve) => {
            nextTick(() => {
                //may be set frozen and redraw on the same tick
                if(unref(globalFrozen)) {
                    updatePending = true;
                    resolve(undefined);
                    return;
                }
                Promise.resolve()
                    .then(() => updateHook.trigger(undefined))
                    .finally(() => {
                        updatePending = false;
                        updateProcessing = undefined;
                        resolve(undefined);
                    });
            });
        });
        return updateProcessing;
    }

    function update() {
        if(unref(globalFrozen)) {
            updatePending = true;
            return Promise.resolve();
        }
        return triggerUpdate();
    }

    watch(() => unref(globalFrozen), (value, old) => {
        if(old && !value && updatePending) {
            triggerUpdate();
        }
    }, debugOptions);

    let offUpdate: OffFunction | undefined;
    function release() {
        offUpdate?.();
    }
    watch(() => toValue(_parent), (value) => {
        release();
        offUpdate = value?.onUpdate(() => { update(); })?.off;
    }, { immediate: true });
    tryOnUnmounted(release);
    if(initialUpdate) {
        tryOnMounted(() => { update(); });
    }

    const state: ComponentState = {
        frozen: globalFrozen,
        freeze,
        update,
        onUpdate: updateHook.on,
    };
    return state;
}
